'use client';

import { Divider } from '@magicschool/spellbook/components/Divider';
import { IconButton, type IconButtonProps } from '@magicschool/spellbook/components/IconButton';
import type { TextInputProps } from '@magicschool/spellbook/components/TextInput';
import { TextInput } from '@magicschool/spellbook/components/TextInput';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { NumberInputUnitSlot } from './slots';

export type { NumberInputUnitSlotProps } from './slots';

export interface NumberInputText {
  /** Accessible label for the decrement button. Defaults to 'Decrement'. */
  decrement?: string;
  /** Accessible label for the increment button. Defaults to 'Increment'. */
  increment?: string;
}

export type NumberInputProps = Omit<TextInputProps, 'type' | 'value' | 'defaultValue' | 'onChange'> & {
  /** Controlled value. When provided, the component operates in controlled mode. */
  value?: number | string;
  /** Uncontrolled default value. */
  defaultValue?: number | string;
  /** Callback fired on every change to the underlying input. */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Minimum allowed value. Disables the decrement button when the current value equals this. */
  min?: number;
  /** Maximum allowed value. Disables the increment button when the current value equals this. */
  max?: number;
  /** Configuration for accessible text labels on stepper buttons (useful for i18n). */
  text?: NumberInputText;
  /**
   * Override the stepper buttons' size independently of the field `size`. Defaults to a size
   * proportional to the field (`md` field → `sm` steppers). Used by `EditableNumberInput` to
   * keep 24px steppers inside its forced-compact field.
   */
  stepperSize?: IconButtonProps['size'];
  /**
   * The id of an external label element. When provided, stepper buttons use `aria-labelledby`
   * combining their own action label with the field label, so screen readers announce e.g.
   * "Decrement, Points" instead of just "Decrement". Matches the pattern used by Select and
   * Autocomplete for label association.
   */
  labelId?: string;
};

// ─── Component ───────────────────────────────────────────────────────────────

const NumberInputBase = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      children,
      className,
      disabled = false,
      id: providedId,
      invalid = false,
      max,
      min,
      readOnly = false,
      size = 'md',
      value,
      defaultValue,
      onChange,
      text = {},
      labelId,
      stepperSize,
      ...props
    },
    forwardedRef,
  ) => {
    const { decrement = 'Decrement', increment = 'Increment' } = text;
    const resolvedStepperSize = stepperSize ?? (size === 'lg' ? 'md' : size === 'xl' ? 'lg' : 'sm');
    const id = useId(providedId);
    const decrementLabelId = `${id}-decrement-label`;
    const incrementLabelId = `${id}-increment-label`;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const liveRegionId = `${id}-live`;
    const [liveAnnouncement, setLiveAnnouncement] = useState('');

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<number | string>(defaultValue ?? '');

    const currentValue = isControlled ? value : internalValue;
    const numericValue = currentValue === '' || currentValue === undefined ? Number.NaN : Number(currentValue);

    const atMin = min !== undefined && !Number.isNaN(numericValue) && numericValue <= min;
    const atMax = max !== undefined && !Number.isNaN(numericValue) && numericValue >= max;

    useImperativeHandle(forwardedRef, () => inputRef.current!, []);

    const { slots, rest } = useSlots(children, {
      unit: { Component: NumberInputUnitSlot, props: { size } },
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Block characters that are valid in scientific notation ('e', '+') but
      // meaningless for this component's integer/decimal use cases.
      if (e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();

      props.onKeyDown?.(e);
    };

    const updateValue = useCallback(
      (direction: 'increment' | 'decrement', event: React.MouseEvent<HTMLButtonElement>) => {
        // Prevent the click from bubbling to TextInput's container onClick, which would steal focus back to the input
        event.stopPropagation();

        const input = inputRef.current;
        if (!input || disabled || readOnly) return;

        if (direction === 'increment') {
          input.stepUp();
        } else {
          input.stepDown();
        }

        // Dispatch a native input event so React's synthetic event system handles
        // it — consumers' onChange receives a full SyntheticEvent with currentTarget,
        // nativeEvent, stopPropagation(), etc. The handleChange listener below also
        // updates internalValue for uncontrolled mode.
        const nativeEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(nativeEvent);

        // Announce immediately via an assertive live region - mirrors the native
        // spinbutton, which interrupts the current speech on each step. Rapid clicks
        // naturally resolve to the final value since each announcement interrupts the last.
        setLiveAnnouncement(input.value);
      },
      [disabled, readOnly],
    );

    return (
      <>
        <TextInput
          data-spellbook-name="number-input"
          ref={inputRef}
          type="number"
          id={id}
          className={cn(
            // Hide native browser spinners — we provide our own stepper buttons
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
            // Remove right padding so stepper buttons sit flush with the border
            !readOnly && 'ltr:pr-1 rtl:pl-1',
            className,
          )}
          disabled={disabled}
          invalid={invalid}
          readOnly={readOnly}
          size={size}
          min={min}
          max={max}
          aria-valuenow={!Number.isNaN(numericValue) ? numericValue : undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          {...(isControlled ? { value } : { defaultValue })}
          onChange={handleChange}
          {...props}
          onKeyDown={handleKeyDown}
        >
          {rest}
          {!readOnly && (
            <TextInput.TrailingAdornment overrideDefault>
              <div className="flex h-full items-stretch">
                {slots.unit}
                <div className="flex rtl:flex-row-reverse items-center h-full">
                  {labelId && (
                    <span id={decrementLabelId} className="sr-only">
                      {decrement}
                    </span>
                  )}
                  <IconButton
                    name="minus"
                    size={resolvedStepperSize}
                    color="neutral"
                    variant="ghost"
                    {...(labelId ? { 'aria-labelledby': `${decrementLabelId} ${labelId}` } : { label: decrement })}
                    disabled={disabled || atMin}
                    type="button"
                    onClick={(e) => updateValue('decrement', e)}
                    className="focus-visible:ring-offset-0"
                  />
                  <div className="self-center flex h-1/2 mx-1">
                    <Divider orientation="vertical" decorative variant="strong" />
                  </div>
                  {labelId && (
                    <span id={incrementLabelId} className="sr-only">
                      {increment}
                    </span>
                  )}
                  <IconButton
                    name="plus"
                    size={resolvedStepperSize}
                    color="neutral"
                    variant="ghost"
                    {...(labelId ? { 'aria-labelledby': `${incrementLabelId} ${labelId}` } : { label: increment })}
                    disabled={disabled || atMax}
                    type="button"
                    onClick={(e) => updateValue('increment', e)}
                    className="focus-visible:ring-offset-0"
                  />
                </div>
              </div>
            </TextInput.TrailingAdornment>
          )}
        </TextInput>
        {/* Live region announces the new value to screen readers after stepper button clicks,
            matching the native spinbutton announcement triggered by up/down arrow keys. */}
        <output id={liveRegionId} aria-live="assertive" aria-atomic="true" className="sr-only">
          {liveAnnouncement}
        </output>
      </>
    );
  },
);

NumberInputBase.displayName = 'NumberInput';

/**
 * Numeric input with decrement/increment stepper buttons.
 *
 * ### When to use
 * - Numeric form fields where users enter quantities, counts, or measured values
 * - When min/max constraints exist and users benefit from visible stepper controls
 *
 * ### When NOT to use
 * - Free-form numeric entry without constraints: use `<TextInput type="number" />` directly
 * - Large range selection: use a range/slider input component instead
 * - Inline editing of a displayed value: use `<EditableNumberInput>` instead
 *
 * ### Interaction model
 * - **Up/Down arrows** → increment/decrement by `step` (default 1)
 * - **Home/End** → jump to `min` / `max` (when set)
 * - **`+` button** → increments; disabled when value equals `max`
 * - **`−` button** → decrements; disabled when value equals `min`
 * - In `readOnly` mode, stepper buttons are hidden entirely
 *
 * ### API
 * - `min` / `max`: Passed to the input; also used to disable stepper buttons at boundaries
 * - `value` / `defaultValue`: Controlled and uncontrolled modes (accepts `number | string`)
 * - `onChange`: Standard input change event handler
 * - `text.decrement` / `text.increment`: Accessible labels for stepper buttons (use for i18n)
 * - `labelId`: Associates stepper buttons with an external label element; screen readers announce e.g. "Increment, Points"
 * - `size`, `disabled`, `readOnly`, `invalid`: Inherited from `TextInput`
 *
 * ### Slots
 * - `<NumberInput.Unit>`: Label rendered between the input value and the stepper buttons (e.g. 'pts', 'kg')
 *
 * ### Accessibility
 * - `type="number"` gives the input an implicit `role="spinbutton"`
 * - `aria-valuenow`, `aria-valuemin`, `aria-valuemax` are set automatically from props
 * - An assertive 'aria-live' region announces the new value on each button click; since focus
 *   moves to the button (not the spinbutton), 'aria-valuenow' alone is not announced. Assertive
 *  * mode interrupts like native spinbutton - rapid clicks resolve to the final value naturally
 * - Stepper buttons are individually labeled via the `text` prop for screen readers
 * - Stepper buttons are `type="button"` to prevent accidental form submission
 * - Native keyboard: Up/Down arrows increment/decrement; Home/End jump to min/max
 * - Pair `invalid` with `aria-describedby` pointing to an error message element
 * - MUST have a visible label via `<Label htmlFor={id}>` or within `Form.Group`
 * - Stepper buttons are hidden (not disabled) in `readOnly` mode
 */
export const NumberInput = Object.assign(NumberInputBase, {
  Unit: NumberInputUnitSlot,
});
